import { useEffect, useState, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Switch, Route, Redirect } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { PlanProvider } from './hooks/usePlan';
import Navbar from './components/Navbar';
import './index.css';
import i18n from './i18n';

const PartList = lazy(() => import('./components/PartList'));
const PartForm = lazy(() => import('./components/PartForm'));
const Login = lazy(() => import('./components/Login'));
const Register = lazy(() => import('./components/Register'));
const CategoryList = lazy(() => import('./components/CategoryList'));
const CategoryForm = lazy(() => import('./components/CategoryForm'));
const WarehouseList = lazy(() => import('./components/WarehouseList'));
const WarehouseForm = lazy(() => import('./components/WarehouseForm'));
const Suppliers = lazy(() => import('./components/Suppliers'));
const SupplierForm = lazy(() => import('./components/SupplierForm'));
const Dashboard = lazy(() => import('./components/Dashboard'));
const Landing = lazy(() => import('./components/Landing'));
const SalesOrderList = lazy(() => import('./components/SalesOrderList'));
const SalesOrderForm = lazy(() => import('./components/SalesOrderForm'));
const GoodsReceiptNoteList = lazy(() => import('./components/GoodsReceiptNoteList'));
const GoodsReceiptNoteForm = lazy(() => import('./components/GoodsReceiptNoteForm'));
const InvoiceList = lazy(() => import('./components/InvoiceList'));
const InvoiceForm = lazy(() => import('./components/InvoiceForm'));
const InvoiceView = lazy(() => import('./components/InvoiceView'));
const CustomerList = lazy(() => import('./components/CustomerList'));
const CustomerForm = lazy(() => import('./components/CustomerForm'));
const ProductSearch = lazy(() => import('./components/ProductSearch'));
const LocationAssign = lazy(() => import('./components/LocationAssign'));
const WarehouseNavigator = lazy(() => import('./components/WarehouseNavigator'));
const WarehouseSetup = lazy(() => import('./components/WarehouseSetup'));
const Chatbot = lazy(() => import('./components/Chatbot'));
const UserList = lazy(() => import('./components/UserList'));
const UserForm = lazy(() => import('./components/UserForm'));
const UserProfile = lazy(() => import('./components/UserProfile'));
const RoleList = lazy(() => import('./components/RoleList'));
const RoleForm = lazy(() => import('./components/RoleForm'));
const Settings = lazy(() => import('./components/Settings'));
const PrivateRoute = lazy(() => import('./components/PrivateRoute'));
const DemoTour = lazy(() => import('./components/DemoTour'));
const OnboardingChecklist = lazy(() => import('./components/OnboardingChecklist'));
const SetupWizard = lazy(() => import('./components/SetupWizard'));
const ReturnList = lazy(() => import('./components/ReturnList'));
const ReturnForm = lazy(() => import('./components/ReturnForm'));
const ReturnView = lazy(() => import('./components/ReturnView'));
const PaymentList = lazy(() => import('./components/PaymentList'));
const PlanManager = lazy(() => import('./components/PlanManager'));
const CompanyProfile = lazy(() => import('./components/CompanyProfile'));
const AdminCompanies = lazy(() => import('./components/AdminCompanies'));
const SmsTemplateManager = lazy(() => import('./components/SmsTemplateManager'));
const Logs = lazy(() => import('./components/Logs'));
const SalesAssistant = lazy(() => import('./components/SalesAssistant'));
const OnlineStore = lazy(() => import('./components/OnlineStore'));
const StoreCheckout = lazy(() => import('./components/StoreCheckout'));
const OnlineOrderManagement = lazy(() => import('./components/OnlineOrderManagement'));
const OrderTracking = lazy(() => import('./components/OrderTracking'));
const StoreList = lazy(() => import('./components/StoreList'));
const PricingPage = lazy(() => import('./components/PricingPage'));

function App() {
  return (
    <Router>
      <AuthProvider>
        <PlanProvider>
          <AppContent />
        </PlanProvider>
      </AuthProvider>
    </Router>
  );
}

function AppContent() {
  const { user, loading } = useAuth();
  const [showWizard, setShowWizard] = useState(false);

  useEffect(() => {
    i18n.changeLanguage('fa');
  }, []);

  useEffect(() => {
    const dir = i18n.language === 'fa' ? 'rtl' : 'ltr';
    document.documentElement.dir = dir;
    document.documentElement.lang = i18n.language;
    const handler = (lng) => {
      document.documentElement.dir = lng === 'fa' ? 'rtl' : 'ltr';
      document.documentElement.lang = lng;
    };
    i18n.on('languageChanged', handler);
    return () => i18n.off('languageChanged', handler);
  }, []);

  useEffect(() => {
    const handler = () => setShowWizard(true);
    window.addEventListener('partbin:open-wizard', handler);
    return () => window.removeEventListener('partbin:open-wizard', handler);
  }, []);

  const openWizard = () => setShowWizard(true);

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="App" dir={i18n.language === 'fa' ? 'rtl' : 'ltr'}>
      <Navbar onOpenWizard={openWizard} />
      <Suspense fallback={null}>
        <DemoTour user={user} />
        <OnboardingChecklist onOpenWizard={openWizard} />
        {showWizard && <SetupWizard onClose={() => setShowWizard(false)} user={user} />}
      </Suspense>
      <main className="w-full max-w-full min-h-[calc(100vh-56px)] overflow-y-auto p-3 md:p-6">
        <Suspense fallback={<div>Loading...</div>}>
          <Switch>
          <Route path="/" exact>
            <Suspense fallback={<div className="text-center p-10 text-gray-500">{i18n.t('loading')}</div>}>
              {user ? <Dashboard /> : <Landing />}
            </Suspense>
          </Route>
          <Route path="/store/:companySlug/checkout" exact component={StoreCheckout} />
          <Route path="/store/:companySlug/track" exact component={OrderTracking} />
          <Route path="/store/:companySlug" exact component={OnlineStore} />
          <Route path="/store" exact>
            {user ? <StoreList /> : <Redirect to="/" />}
          </Route>
          <PrivateRoute path="/parts" exact component={PartList} />
          <PrivateRoute path="/parts/new" component={PartForm} roles={['admin', 'staff']} />
          <PrivateRoute path="/parts/edit/:id" component={PartForm} roles={['admin', 'staff']} />
          <PrivateRoute path="/product-search" component={ProductSearch} />
          <PrivateRoute path="/location-assign" component={LocationAssign} />
          <PrivateRoute path="/warehouse-navigator" component={WarehouseNavigator} />
          <PrivateRoute path="/chatbot" component={Chatbot} />
          <PrivateRoute path="/sales-insights" component={SalesAssistant} />
          <PrivateRoute path="/categories" exact component={CategoryList} />
          <PrivateRoute path="/categories/new" component={CategoryForm} roles={['admin', 'staff']} />
          <PrivateRoute path="/categories/edit/:id" component={CategoryForm} roles={['admin', 'staff']} />
          <PrivateRoute path="/warehouses" exact component={WarehouseList} />
          <PrivateRoute path="/warehouses/new" component={WarehouseForm} roles={['admin', 'staff']} />
          <PrivateRoute path="/warehouses/edit/:warehouseId" component={WarehouseForm} roles={['admin', 'staff']} />
          <PrivateRoute path="/warehouses/:warehouseId/setup" component={WarehouseSetup} roles={['admin', 'staff']} />
          <PrivateRoute path="/suppliers" exact component={Suppliers} />
          <PrivateRoute path="/suppliers/edit/:id" component={SupplierForm} roles={['admin', 'staff']} />
          <PrivateRoute path="/sales-orders" exact component={SalesOrderList} />
          <PrivateRoute path="/online-orders" component={OnlineOrderManagement} />
          <PrivateRoute path="/sales-orders/new" component={SalesOrderForm} roles={['admin', 'staff']} />
          <PrivateRoute path="/sales-orders/edit/:id" component={SalesOrderForm} roles={['admin', 'staff']} />


          <PrivateRoute path="/users" exact component={UserList} roles={['admin']} />
          <PrivateRoute path="/users/new" component={UserForm} roles={['admin']} />
          <PrivateRoute path="/users/edit/:id" component={UserForm} roles={['admin']} />

          <PrivateRoute path="/goods-receipt-notes" exact component={GoodsReceiptNoteList} />
          <PrivateRoute path="/goods-receipt-notes/new" component={GoodsReceiptNoteForm} roles={['admin', 'staff']} />
          <PrivateRoute path="/goods-receipt-notes/edit/:id" component={GoodsReceiptNoteForm} roles={['admin', 'staff']} />

          <PrivateRoute path="/invoices" exact component={InvoiceList} />
          <PrivateRoute path="/invoices/new" component={InvoiceForm} roles={['admin', 'staff']} />
          <PrivateRoute path="/invoices/edit/:id" component={InvoiceForm} roles={['admin', 'staff']} />
          <PrivateRoute path="/invoices/view/:id" component={InvoiceView} />

          <PrivateRoute path="/customers" exact component={CustomerList} />
          <PrivateRoute path="/customers/new" component={CustomerForm} roles={['admin', 'staff']} />
          <PrivateRoute path="/customers/edit/:id" component={CustomerForm} roles={['admin', 'staff']} />




          <PrivateRoute path="/roles" exact component={RoleList} roles={['admin']} />
          <PrivateRoute path="/roles/new" component={RoleForm} roles={['admin']} />
          <PrivateRoute path="/roles/edit/:id" component={RoleForm} roles={['admin']} />
          <PrivateRoute path="/returns" exact component={ReturnList} />
          <PrivateRoute path="/returns/new" component={ReturnForm} roles={['admin', 'staff']} />
          <PrivateRoute path="/returns/:id" component={ReturnView} />
          <PrivateRoute path="/payments" component={PaymentList} />
          <PrivateRoute path="/profile" component={UserProfile} />
          <PrivateRoute path="/company-profile" component={CompanyProfile} />
          <PrivateRoute path="/settings" component={Settings} />
          <PrivateRoute path="/plan-manager" component={PlanManager} roles={['admin']} />
          <PrivateRoute path="/admin/companies" component={AdminCompanies} roles={['admin']} />
          <PrivateRoute path="/admin/sms-templates" component={SmsTemplateManager} roles={['admin']} />
          <PrivateRoute path="/admin/logs" component={Logs} roles={['admin']} />
          <Route path="/login">
            {user ? <Redirect to="/" /> : <Login />}
          </Route>
          <Route path="/register" component={Register} />
          <Route path="/pricing" component={PricingPage} />
        </Switch>
        </Suspense>
      </main>
    </div>
  );
}

export default App;
